import {
  DownOutlined,
  PrinterOutlined,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  PageHeader,
  Select,
  Space,
  Table
} from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'antd/lib/form/Form';
import dayjs from 'dayjs';
import moment from 'moment';
import { UserShippingRecordsSearchQuery } from '../../../custom_types/order-page';
import {
  fetchOrdersHandler,
  fetchOrdersHandlers,
  selectLoading,
  selectOrders
} from '../../../redux/orders/ordersSlice';
import NoData from '../../../shared/components/NoData';
import {
  CARRIERS,
  Country,
  COUNTRY_NAMES
} from '../../../shared/utils/constants';
import columns, {
  ShipmentStatus,
  ShippingRecord
} from '../components/TableColumns/columns';
import {
  downloadLabelsHandler,
  downloadShipmentForms,
  opentLabelUrlHandler
} from '../../../shared/utils/helpers';
import { selectClientAccounts } from '../../../redux/settings/carriersSlice';

const SearchOrderPage = (): ReactElement => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const clientAccounts = useSelector(selectClientAccounts);
  const orders = useSelector(selectOrders);
  const loading = useSelector(selectLoading);

  const [startDate, setStartDate] = useState<string>(
    dayjs().subtract(1, 'day').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  // ===================== 新增：统计汇总 =====================
  const getSummary = () => {
    if (!orders || orders.length === 0) {
      return { total: 0, weight: 0, amount: 0 };
    }

    const KG_TO_LB = 2.20462;
    const totalOrders = orders.length;
    let totalWeight = 0;
    let totalAmount = 0;

    orders.forEach((item) => {
      // ===================== 安全计算重量（从 packageList 取）=====================
      let w = 0;
      if (item.packageList && item.packageList.length > 0) {
        const pkg = item.packageList[0]; // 取第一个包裹
        if (pkg.weight) {
          const weightValue = Number(pkg.weight.value) || 0;
          const weightUnit = pkg.weight.unitOfMeasure || 'LB';

          w = weightValue;
          if (weightUnit.toUpperCase() === 'KG') {
            w *= KG_TO_LB;
          }
        }
      }
      totalWeight += w;

      // ===================== 安全运费 =====================
      totalAmount += Number(item.rate?.amount) || 0;
    });

    return {
      total: totalOrders,
      weight: totalWeight.toFixed(2),
      amount: totalAmount.toFixed(2)
    };
  };

  const summary = getSummary();
  // ==========================================================

  // 查询
  const searchRecords = () => {
    form.validateFields().then((values) => {
      const searchValues: UserShippingRecordsSearchQuery = {
        ...values,
        startDate,
        endDate,
        country: values.country,
        carrierAccount: values.carrierAccount
      };
      dispatch(fetchOrdersHandlers(searchValues));
    });
  };

  const handleMenuClick = (e: any) => {
    window.open(e.key, '_blank');
  };

  // 打印面单按钮
  const displayLablePrintButton = (record: ShippingRecord) => {
    if (record.carrier === CARRIERS.MAO_YUAN) {
      return (
        <Dropdown
          overlay={
            <Menu onClick={handleMenuClick}>
              {record.labelUrlList?.map((url, index) => (
                <Menu.Item key={url.labelUrl}>
                  {`打印面单${index + 1}`}
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button type="primary" icon={<PrinterOutlined />}>
            打印面单 <DownOutlined />
          </Button>
        </Dropdown>
      );
    }
    return (
      <Button
        type="primary"
        icon={<PrinterOutlined />}
        onClick={() =>
          record.carrier === CARRIERS.RUI_YUN ||
          record.carrier === CARRIERS.USPS3 ||
          record.carrier === CARRIERS.DPD ||
          record.carrier === CARRIERS.Wang_Cang ||
          record.carrier === CARRIERS.Lyt ||
          record.carrier === CARRIERS.Ymx ||
          record.carrier === CARRIERS.HYD ||
          record.carrier === CARRIERS.KAPAI ||
          record.carrier === CARRIERS.HUALEI
            ? opentLabelUrlHandler(record)
            : downloadLabelsHandler(record)
        }
        ghost
      >
        打印面单
      </Button>
    );
  };

  return (
    <div>
      <PageHeader
        title={<FormattedMessage id="searchOrders" defaultMessage="订单查询" />}
        extra={[
          <Button
            key="search"
            type="primary"
            icon={<SearchOutlined />}
            onClick={searchRecords}
          >
            查询
          </Button>
        ]}
      >
        <Form form={form} layout="horizontal">
          <Space direction="horizontal" size="middle" wrap>
            <Form.Item label="开始日期" name="startDate">
              <DatePicker
                defaultValue={moment(startDate)}
                onChange={(_, dateString) => setStartDate(dateString)}
              />
            </Form.Item>
            <Form.Item label="结束日期" name="endDate">
              <DatePicker
                defaultValue={moment(endDate)}
                onChange={(_, dateString) => setEndDate(dateString)}
              />
            </Form.Item>
            <Form.Item label="国家" name="country">
              <Select placeholder="选择国家" style={{ width: 120 }} allowClear>
                <Select.Option value={Country.USA}>
                  {COUNTRY_NAMES[Country.USA]}
                </Select.Option>
                <Select.Option value={Country.CHINA}>
                  {COUNTRY_NAMES[Country.CHINA]}
                </Select.Option>
                <Select.Option value={Country.AUSTRALIA}>
                  {COUNTRY_NAMES[Country.AUSTRALIA]}
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="物流渠道" name="carrierAccount">
              <Select style={{ width: 220 }}>
                {clientAccounts.map((account) => (
                  <Select.Option key={account.id} value={account.accountId}>
                    {account.accountId}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space>
        </Form>
      </PageHeader>

      {/* ===================== 新增：汇总统计栏（红框位置） ===================== */}
      <div
        style={{
          background: '#fff',
          padding: '12px 24px',
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          gap: '40px',
          fontSize: '14px',
          marginBottom: 10
        }}
      >
        <div>
          <strong>总订单：</strong> {summary.total}
        </div>
        <div>
          <strong>总重量：</strong> {summary.weight} lb
        </div>
        <div>
          <strong>总价格：</strong> $ {summary.amount}
        </div>
      </div>
      {/* ====================================================================== */}

      <Table<ShippingRecord>
        scroll={{ x: 'max-content' }}
        rowKey={(record) => record.id}
        columns={[
          ...columns,
          {
            title: '操作',
            key: 'action',
            render: (_val, record: ShippingRecord) => (
              <Space direction="vertical">
                {ShipmentStatus.FULFILLED === record.status &&
                  displayLablePrintButton(record)}
                {record.forms && record.forms.length > 0 && (
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={() => downloadShipmentForms(record)}
                    ghost
                  >
                    打印发票
                  </Button>
                )}
              </Space>
            )
          }
        ]}
        dataSource={orders}
        loading={loading}
        locale={{ emptyText: <NoData /> }}
      />
    </div>
  );
};

export default SearchOrderPage;
